import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Pagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import '../styles/PreApprovalMaturityPage.css';

const PreApprovalMaturityPage = () => {
  // State for Available for Pre-Approval section
  const [availableDeals, setAvailableDeals] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState('');
  
  // State for Pre-Approval Blotter section
  const [blotterDeals, setBlotterDeals] = useState([]);
  const [blotterLoading, setBlotterLoading] = useState(false);
  const [blotterError, setBlotterError] = useState('');
  
  // Common filters
  const [productType, setProductType] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [counterparty, setCounterparty] = useState('');
  const [status, setStatus] = useState('not_pre_approved');
  
  // Dialog states
  const [preApproveDialog, setPreApproveDialog] = useState({ open: false, deal: null });
  const [approveDialog, setApproveDialog] = useState({ open: false, deal: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, deal: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Pagination
  const [availablePage, setAvailablePage] = useState(1);
  const [blotterPage, setBlotterPage] = useState(1);
  const itemsPerPage = 10;
  
  // User info for authorization
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    setUserRole(userData.role || '');
    fetchAvailableDeals();
    fetchBlotterDeals();
  }, [productType, startDate, endDate, counterparty, status]);
  
  const fetchAvailableDeals = async () => {
    setAvailableLoading(true);
    setAvailableError('');
    try {
      const params = new URLSearchParams();
      if (productType !== 'all') params.append('productType', productType);
      if (startDate && endDate) {
        params.append('dateRange', `${startDate.toISOString().split('T')[0]},${endDate.toISOString().split('T')[0]}`);
      }
      if (counterparty) params.append('counterparty', counterparty);
      if (status) params.append('status', status);
      
      const response = await axios.get(`${API_BASE_URL}/maturity/pre-approval/deals?${params}`);
      
      if (response.data.success) {
        setAvailableDeals(response.data.data || []);
      } else {
        setAvailableError(response.data.error || 'Failed to fetch deals');
      }
    } catch (err) {
      setAvailableError('Error fetching deals: ' + (err.response?.data?.error || err.message));
      setAvailableDeals([]);
    } finally {
      setAvailableLoading(false);
    }
  };
  
  const fetchBlotterDeals = async () => {
    setBlotterLoading(true);
    setBlotterError('');
    try {
      const params = new URLSearchParams();
      if (productType !== 'all') params.append('productType', productType);
      if (startDate && endDate) {
        params.append('dateRange', `${startDate.toISOString().split('T')[0]},${endDate.toISOString().split('T')[0]}`);
      }
      if (counterparty) params.append('counterparty', counterparty);
      
      const response = await axios.get(`${API_BASE_URL}/maturity/pre-approval/blotter?${params}`);
      
      if (response.data.success) {
        setBlotterDeals(response.data.data || []);
      } else {
        setBlotterError(response.data.error || 'Failed to fetch blotter deals');
      }
    } catch (err) {
      setBlotterError('Error fetching blotter deals: ' + (err.response?.data?.error || err.message));
      setBlotterDeals([]);
    } finally {
      setBlotterLoading(false);
    }
  };
  
  const handlePreApprove = async () => {
    if (!preApproveDialog.deal) return;
    
    setActionLoading(true);
    try {
      const deal = preApproveDialog.deal;
      const response = await axios.post(
        `${API_BASE_URL}/maturity/pre-approval/${deal.product_type}/${deal.id}`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage(`Deal ${deal.deal_number} has been pre-approved and elevated to authorizer`);
        setPreApproveDialog({ open: false, deal: null });
        fetchAvailableDeals();
        fetchBlotterDeals();
      } else {
        setAvailableError(response.data.error || 'Failed to pre-approve deal');
      }
    } catch (err) {
      setAvailableError('Error pre-approving deal: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleApprovePreApproval = async () => {
    if (!approveDialog.deal) return;
    
    setActionLoading(true);
    try {
      const deal = approveDialog.deal;
      const response = await axios.put(
        `${API_BASE_URL}/maturity/pre-approval/${deal.product_type}/${deal.id}/approve`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage(`Pre-approval for deal ${deal.deal_number} has been approved`);
        setApproveDialog({ open: false, deal: null });
        fetchAvailableDeals();
        fetchBlotterDeals();
      } else {
        setBlotterError(response.data.error || 'Failed to approve pre-approval');
      }
    } catch (err) {
      setBlotterError('Error approving pre-approval: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRejectPreApproval = async () => {
    if (!rejectDialog.deal) return;
    
    setActionLoading(true);
    try {
      const deal = rejectDialog.deal;
      const response = await axios.put(
        `${API_BASE_URL}/maturity/pre-approval/${deal.product_type}/${deal.id}/reject`,
        {},
        {
          headers: {
            'x-user-data': JSON.stringify(user)
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage(`Pre-approval for deal ${deal.deal_number} has been rejected`);
        setRejectDialog({ open: false, deal: null });
        fetchAvailableDeals();
        fetchBlotterDeals();
      } else {
        setBlotterError(response.data.error || 'Failed to reject pre-approval');
      }
    } catch (err) {
      setBlotterError('Error rejecting pre-approval: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '0.00';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };
  
  const getProductTypeLabel = (type) => {
    const labels = {
      'gsec': 'GSEC',
      'money_market': 'Money Market',
      'fixed_deposit': 'Fixed Deposit',
      'repo': 'Repo'
    };
    return labels[type] || type;
  };
  
  const getStatusChip = (deal) => {
    if (deal.pre_approval_status === 'pre_approved') {
      return <Chip label="Pre-Approved" color="success" size="small" />;
    } else if (deal.pre_approval_status === 'pre_approved_pending') {
      return <Chip label="Pending Authorization" color="warning" size="small" />;
    } else if (deal.pre_approval_status === 'rejected') {
      return <Chip label="Rejected" color="error" size="small" />;
    } else {
      return <Chip label="Not Pre-Approved" color="default" size="small" />;
    }
  };
  
  // Check if user can pre-approve (any authenticated user)
  const canPreApprove = () => {
    return user && user.id;
  };
  
  // Check if user can approve/reject pre-approvals (authorizers only)
  const canAuthorize = () => {
    return userRole === 'back_office_final' || userRole === 'admin';
  };
  
  // Pagination helpers
  const getAvailablePaginatedDeals = () => {
    const start = (availablePage - 1) * itemsPerPage;
    return availableDeals.slice(start, start + itemsPerPage);
  };
  
  const getBlotterPaginatedDeals = () => {
    const start = (blotterPage - 1) * itemsPerPage;
    return blotterDeals.slice(start, start + itemsPerPage);
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="ui-page preapproval-page" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom>
          Pre-Approval Maturity Management
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Pre-approve final approved deals and manage pre-approval workflow
        </Typography>
        
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {/* Filters */}
        <Card className="ui-card preapproval-card" sx={{ mb: 3 }}>
          <CardContent className="ui-card-body">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Product Type</InputLabel>
                  <Select
                    value={productType}
                    label="Product Type"
                    onChange={(e) => setProductType(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="gsec">GSEC</MenuItem>
                    <MenuItem value="money_market">Money Market</MenuItem>
                    <MenuItem value="fixed_deposit">Fixed Deposit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Counterparty"
                  value={counterparty}
                  onChange={(e) => setCounterparty(e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="not_pre_approved">Not Pre-Approved</MenuItem>
                    <MenuItem value="pre_approved_pending">Pending Authorization</MenuItem>
                    <MenuItem value="pre_approved">Pre-Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    fetchAvailableDeals();
                    fetchBlotterDeals();
                  }}
                  fullWidth
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {/* Section 1: Available for Pre-Approval */}
        <Card className="ui-card preapproval-card" sx={{ mb: 3 }}>
          <CardContent className="ui-card-body">
            <Typography variant="h6" gutterBottom>
              Available for Pre-Approval
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Final approved deals that can be pre-approved
            </Typography>
            
            {availableError && (
              <Alert severity="error" sx={{ mb: 2 }}>{availableError}</Alert>
            )}
            
            {availableLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer className="ui-table-wrap">
                  <Table size="small" className="ui-table preapproval-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Deal Number</TableCell>
                        <TableCell>Product Type</TableCell>
                        <TableCell>ISIN</TableCell>
                        <TableCell>Counterparty</TableCell>
                        <TableCell>Face Value</TableCell>
                        <TableCell>Maturity Date</TableCell>
                        <TableCell>Days to Maturity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getAvailablePaginatedDeals().map((deal) => (
                        <TableRow key={`${deal.product_type}-${deal.id}`}>
                          <TableCell>{deal.deal_number || deal.id}</TableCell>
                          <TableCell>{getProductTypeLabel(deal.product_type)}</TableCell>
                          <TableCell>{deal.isin || '-'}</TableCell>
                          <TableCell>{deal.counterparty_name || deal.counterparty || '-'}</TableCell>
                          <TableCell>{formatCurrency(deal.face_value)}</TableCell>
                          <TableCell>{formatDate(deal.maturity_date)}</TableCell>
                          <TableCell>{deal.days_to_maturity || '-'}</TableCell>
                          <TableCell>{getStatusChip(deal)}</TableCell>
                          <TableCell>
                            {canPreApprove() && deal.pre_approval_status !== 'pre_approved_pending' && deal.pre_approval_status !== 'pre_approved' && (
                              <Tooltip title="Pre-Approve">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setPreApproveDialog({ open: true, deal })}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {deal.pre_approval_status === 'pre_approved_pending' && canAuthorize() && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => setApproveDialog({ open: true, deal })}
                                  >
                                    <ThumbUpIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setRejectDialog({ open: true, deal })}
                                  >
                                    <ThumbDownIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {getAvailablePaginatedDeals().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No deals available for pre-approval
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {availableDeals.length > itemsPerPage && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(availableDeals.length / itemsPerPage)}
                      page={availablePage}
                      onChange={(e, value) => setAvailablePage(value)}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Section 2: Pre-Approval Blotter */}
        <Card className="ui-card preapproval-card">
          <CardContent className="ui-card-body">
            <Typography variant="h6" gutterBottom>
              Pre-Approval Blotter
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Deals with pre_approval_status = 'pre_approved'
            </Typography>
            
            {blotterError && (
              <Alert severity="error" sx={{ mb: 2 }}>{blotterError}</Alert>
            )}
            
            {blotterLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer className="ui-table-wrap">
                  <Table size="small" className="ui-table preapproval-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Deal Number</TableCell>
                        <TableCell>Product Type</TableCell>
                        <TableCell>ISIN</TableCell>
                        <TableCell>Counterparty</TableCell>
                        <TableCell>Face Value</TableCell>
                        <TableCell>Maturity Date</TableCell>
                        <TableCell>Days to Maturity</TableCell>
                        <TableCell>Pre-Approved At</TableCell>
                        <TableCell>Authorized At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getBlotterPaginatedDeals().map((deal) => (
                        <TableRow key={`${deal.product_type}-${deal.id}`}>
                          <TableCell>{deal.deal_number || deal.id}</TableCell>
                          <TableCell>{getProductTypeLabel(deal.product_type)}</TableCell>
                          <TableCell>{deal.isin || '-'}</TableCell>
                          <TableCell>{deal.counterparty_name || deal.counterparty || '-'}</TableCell>
                          <TableCell>{formatCurrency(deal.face_value)}</TableCell>
                          <TableCell>{formatDate(deal.maturity_date)}</TableCell>
                          <TableCell>{deal.days_to_maturity || '-'}</TableCell>
                          <TableCell>{formatDate(deal.pre_approved_at)}</TableCell>
                          <TableCell>{formatDate(deal.pre_approval_authorized_at)}</TableCell>
                        </TableRow>
                      ))}
                      {getBlotterPaginatedDeals().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No pre-approved deals found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {blotterDeals.length > itemsPerPage && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(blotterDeals.length / itemsPerPage)}
                      page={blotterPage}
                      onChange={(e, value) => setBlotterPage(value)}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Pre-Approve Dialog */}
        <Dialog open={preApproveDialog.open} onClose={() => setPreApproveDialog({ open: false, deal: null })}>
          <DialogTitle>Pre-Approve Deal</DialogTitle>
          <DialogContent>
            {preApproveDialog.deal && (
              <Box>
                <Typography>Are you sure you want to pre-approve this deal?</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Deal Number: {preApproveDialog.deal.deal_number || preApproveDialog.deal.id}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Product Type: {getProductTypeLabel(preApproveDialog.deal.product_type)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Face Value: {formatCurrency(preApproveDialog.deal.face_value)}
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  This will mark the deal as pre-approved and elevate it to an authorizer for final approval.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreApproveDialog({ open: false, deal: null })}>Cancel</Button>
            <Button onClick={handlePreApprove} variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Pre-Approve'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Approve Pre-Approval Dialog */}
        <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, deal: null })}>
          <DialogTitle>Approve Pre-Approval</DialogTitle>
          <DialogContent>
            {approveDialog.deal && (
              <Box>
                <Typography>Are you sure you want to approve this pre-approval?</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Deal Number: {approveDialog.deal.deal_number || approveDialog.deal.id}
                </Typography>
                <Alert severity="success" sx={{ mt: 2 }}>
                  This will finalize the pre-approval and the deal will appear in the pre-approval blotter.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialog({ open: false, deal: null })}>Cancel</Button>
            <Button onClick={handleApprovePreApproval} variant="contained" color="success" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Reject Pre-Approval Dialog */}
        <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, deal: null })}>
          <DialogTitle>Reject Pre-Approval</DialogTitle>
          <DialogContent>
            {rejectDialog.deal && (
              <Box>
                <Typography>Are you sure you want to reject this pre-approval?</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Deal Number: {rejectDialog.deal.deal_number || rejectDialog.deal.id}
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This will reject the pre-approval and reset the deal's pre-approval status.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialog({ open: false, deal: null })}>Cancel</Button>
            <Button onClick={handleRejectPreApproval} variant="contained" color="error" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PreApprovalMaturityPage;
